import { AfterViewInit, Component, computed, effect, input, OnDestroy, output, signal, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IonRouterOutlet } from '@ionic/angular';
import { Subject, takeUntil } from 'rxjs';
import { DriverResponse } from 'src/app/models/driver.model';
import { MenuItem, MenuTabs } from 'src/app/models/menu.model';
import { CoreService } from 'src/app/services/core/core.service';
import { MenuProvider } from 'src/app/services/providers/menu-provider/menu-provider';
import { NotificationProvider } from 'src/app/services/providers/notification-provider/notification-provider';
import { RouterOutletRefService } from 'src/app/services/router-outlet-ref/router-outlet-ref.service';
import { UserProvider } from 'src/app/services/providers/user-provider.ts/user-provider';
import { SqliteStorageService } from 'src/app/services/sqlite-storage/sqlite-storage.service';

@Component({
  selector: 'app-mobile-menu',
  templateUrl: './mobile-menu.component.html',
  styleUrls: ['./mobile-menu.component.scss'],
  standalone: false,
})
export class MobileMenuComponent implements AfterViewInit, OnDestroy {

  @ViewChild(IonRouterOutlet, { static: false }) routerOutlet!: IonRouterOutlet;

  /* Tracks the currently active menu identifier */
  storedmenuId = '0';

  /* Controls visibility of the popup menu */
  showPopup = false;

  isIos: boolean = this.coreService.isIos;

  swipeGestureEnabled = !this.isIos;
  private readonly ROOT_PAGES = '/dashboard';
  private readonly destroy$ = new Subject<void>();

  /* Static bottom menu configuration */
  staticMenuItems = input<MenuTabs[]>([]);

  /* Driver-specific menu configuration */
  driverMenus = input<MenuItem[]>([]);

  /* Dynamic popup menu tabs */
  menuTabs = input<MenuTabs[]>([]);

  /* Unread notification count for badge display */
  unreadCount = computed(() =>
    this.notificationProvider.readUnreadNotificationsCount()
  );

  /* Indicates whether the user is authenticated */
  isLoggedIn = input<boolean>(false);

  /* Emits logout intent to the parent container */
  emitLogOut = output<void>();

  showMobileMenu = signal<boolean>(false);
  menuDisabled = computed(() => this.userProvider.readMenuDisabled());

  constructor(
    private readonly sqliteStorageService: SqliteStorageService,
    private readonly coreService: CoreService,
    private readonly router: Router,
    private readonly userProvider: UserProvider,
    private readonly menuProvider: MenuProvider,
    private readonly notificationProvider: NotificationProvider,
    private readonly routerOutletRefService: RouterOutletRefService,
  ) {
    this.handleEffects();
  }

  ngAfterViewInit(): void {
    if (this.routerOutlet) {
      this.routerOutletRefService.register(this.routerOutlet);
      this.updateSwipeBackState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleEffects() {
    effect(() => {
      if (!this.userProvider.readIsLoggedIn()) {
        this.resetState();
      }
    })

    effect(() => {
      this.showMobileMenu.set(this.menuProvider.readShowMobileMenu());
    })

    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.updateSwipeBackState();
        }
      });
  }

  private updateSwipeBackState(): void {
    if (!this.routerOutlet) {
      return;
    }

    if (!this.isIos) {
      this.swipeGestureEnabled = true;
      this.routerOutlet.swipeGesture = true;
      return;
    }

    const currentPath = this.router.url.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
    const canGoBack = this.routerOutlet.canGoBack(1);

    this.swipeGestureEnabled = canGoBack && currentPath !== this.ROOT_PAGES;
    this.routerOutlet.swipeGesture = this.swipeGestureEnabled;

  }

  /* Converts string-based sequence values to numbers */
  parseNumber(value: string): number {
    return Number.parseInt(value);
  }

  /* Updates active menu state for static tabs and closes popup */
  staticTabMenuId(id: string): void {
    this.storedmenuId = id;
    this.showPopup = false;
  }

  /* Loads and prepares popup menu items before toggling visibility */
  async openMenu(): Promise<void> {
    const driverData =
      await this.sqliteStorageService.getItem<DriverResponse>('meData');

    if (!driverData) {
      return;
    }

    const menuStore = this.driverMenus();
    if (!menuStore) {
      return;
    }

    this.coreService.getSideMenus().then(res => {
      if (!res) {
        return;
      }

      const dashboardTabs =
        this.coreService.getUserTabs(menuStore, res);

      this.userProvider.setMenuTabs(
        dashboardTabs.filter(tab => Number(tab.sequence) > 3)
      );
    });

    this.showPopup = !this.showPopup;
  }

  /* Handles popup menu selection and delegates navigation */
  selectmenuclose(sequenceNo: string, url: string, id: string): void {
    this.storedmenuId = id;
    this.naviagteTab(id, sequenceNo, url);
  }

  /* Performs navigation or toggles popup based on tab sequence */
  naviagteTab(id: string, sequenceNo: string, url: string): void {
    if (this.parseNumber(sequenceNo) !== 3) {
      this.storedmenuId = id;
      this.router.navigateByUrl(url);
      this.showPopup = false;
      return;
    }

    this.showPopup = !this.showPopup;
  }

  /* Triggers logout action */
  logout(): void {
    this.showPopup = false;
    this.emitLogOut.emit();
  }

  /* Ionic lifecycle hook invoked before the view is destroyed */
  ionViewWillLeave(): void {
    this.resetState();
  }

  /* Resets all local component state to default values */
  private resetState(): void {
    this.storedmenuId = '0';
    this.showPopup = false;
  }
}
